// TableLabelVisibilityContext.tsx

import React, { ReactNode, createContext, useContext, useState } from 'react';

export interface ContextTableLabel {
  id: string;
  label: string;
  sortable?: boolean;
  isVisible: boolean;
}

export interface TableLabel {
  id: string;
  label: string;
  sortable?: boolean;
  hidden?: boolean;
}

interface TableLabelVisibilityContextProps {
  tableLabels: ContextTableLabel[];
  setTableLabels: (labels: ContextTableLabel[]) => void;
  autoHideLabels: boolean;
  setAutoHideLabels: (autoHide: boolean) => void;
}

/**
 * TableLabelVisibilityContext
 *
 * A React context for managing the visibility of table labels throughout the application.
 * It provides a centralized state for table labels, including their visibility status, and
 * an option to automatically hide labels based on certain conditions.
 *
 * ## Context Structure
 * - `tableLabels`: An array of `ContextTableLabel` objects representing each table label's state.
 * - `setTableLabels`: A method to update the state of `tableLabels`.
 * - `autoHideLabels`: A boolean state indicating if labels should be automatically hidden.
 * - `setAutoHideLabels`: A method to update the `autoHideLabels` state.
 *
 * ## Usage
 * Wrap your component tree with `TableLabelVisibilityProvider` to provide the context state.
 * Use `useTableLabelVisibility` hook to access and modify the context state within your components.
 *
 * @example
 * ```jsx
 * <TableLabelVisibilityProvider initialTableLabels={labels}>
 *   <YourComponent />
 * </TableLabelVisibilityProvider>
 * ```
 */
const TableLabelVisibilityContext = createContext<TableLabelVisibilityContextProps | null>(null);

/**
 * TableLabelVisibilityProvider Component
 *
 * A context provider component for `TableLabelVisibilityContext`. It initializes the context state
 * with the given table labels and provides the context to its children.
 *
 * @param {ProviderProps} props - Props for the provider including `initialTableLabels` and children.
 * @returns The `TableLabelVisibilityProvider` component.
 */
export const useTableLabelVisibility = () => {
  const context = useContext(TableLabelVisibilityContext);
  if (!context) {
    throw new Error('useTableLabelVisibility must be used within a TableLabelVisibilityProvider');
  }
  return context;
};

interface ProviderProps {
  children: ReactNode;
  initialTableLabels: TableLabel[];
}

export const TableLabelVisibilityProvider: React.FC<ProviderProps> = ({
  children,
  initialTableLabels,
}) => {
  const [tableLabels, setTableLabels] = useState<ContextTableLabel[]>(
    initialTableLabels.map((label) => ({
      ...label,
      isVisible: !label.hidden,
    }))
  );
  const [autoHideLabels, setAutoHideLabels] = useState(true);

  return (
    <TableLabelVisibilityContext.Provider
      value={{ tableLabels, setTableLabels, autoHideLabels, setAutoHideLabels }}
    >
      {children}
    </TableLabelVisibilityContext.Provider>
  );
};
